const POLL_MS = 100;
const STABLE_CHECKS = 4;
const MAX_WAIT_MS = 10000;
const USER_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"] as const;

// The header is taller at the page top but shrinks once scrolled, so the
// offset is capped to the compact height it has at the scroll target.
const SCROLLED_HEADER_HEIGHT = 60;

/** Space between the viewport top and an anchored element (the fixed header). */
export const getAnchorOffset = () => {
	const header = document.querySelector("header")?.getBoundingClientRect().bottom ?? 0;
	return Math.min(Math.max(0, header), SCROLLED_HEADER_HEIGHT);
};

/** Scroll position that places the element right below the fixed header. */
export const getAnchorTop = (el: Element) =>
	Math.max(0, el.getBoundingClientRect().top + window.scrollY - getAnchorOffset());

/**
 * The header height changes while scrolling (and differs per device), so the target is measured
 * again once the scroll has finished and nudged until it sits right below the final header.
 */
const correctAfterScroll = (el: Element, instant: boolean) => {
	let done = false;
	const fix = () => {
		if (done) return;
		done = true;
		window.removeEventListener("scrollend", fix);
		const header = document.querySelector("header")?.getBoundingClientRect().bottom ?? 0;
		const diff = el.getBoundingClientRect().top - Math.max(0, header);
		if (Math.abs(diff) > 2) window.scrollBy({ top: diff, behavior: "instant" });
	};
	if (instant) {
		setTimeout(fix, 400);
		return;
	}
	window.addEventListener("scrollend", fix, { once: true });
	setTimeout(fix, 1500);
};

let cancelActive: (() => void) | null = null;

const prefersReducedMotion = () =>
	window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Scrolls once to the element with the given id, directly below the fixed header.
 * Content is rendered client-side and images shift the layout, so the scroll
 * only starts after the element exists and its position stopped changing.
 * Any new call or user input cancels a pending scroll. `instant` jumps without animation (initial page load).
 */
export const scrollToAnchor = (hash: string, { instant = false } = {}) => {
	cancelActive?.();
	const id = decodeURIComponent(hash.replace(/^#/, ""));
	if (!id) return;

	let timer: ReturnType<typeof setTimeout> | undefined;
	const cancel = () => {
		if (timer) clearTimeout(timer);
		for (const e of USER_EVENTS) window.removeEventListener(e, cancel);
		if (cancelActive === cancel) cancelActive = null;
	};
	cancelActive = cancel;
	for (const e of USER_EVENTS)
		window.addEventListener(e, cancel, { passive: true });

	const startedAt = Date.now();
	let lastPos = Number.NaN;
	let stable = 0;

	const check = () => {
		const el = document.getElementById(id);
		if (el) {
			const pos = Math.round(el.getBoundingClientRect().top + window.scrollY);
			stable = pos === lastPos ? stable + 1 : 0;
			lastPos = pos;
			if (stable >= STABLE_CHECKS) {
				window.scrollTo({
					top: getAnchorTop(el),
					// "auto" would follow the html scroll-behavior: smooth
					behavior: instant || prefersReducedMotion() ? "instant" : "smooth",
				});
				cancel();
				correctAfterScroll(el, instant);
				return;
			}
		}
		if (Date.now() - startedAt < MAX_WAIT_MS) {
			timer = setTimeout(check, POLL_MS);
		} else {
			cancel();
		}
	};
	check();
};

/**
 * Navigates to a CMS link. Links to an anchor on the current page scroll
 * client-side, since Gatsby does not trigger a route update for them.
 */
export const navigateOrScroll = (link: string, navigate: (to: string) => void) => {
	const url = new URL(link, window.location.href);
	const samePage =
		url.origin === window.location.origin &&
		url.pathname.replace(/\/$/, "") === window.location.pathname.replace(/\/$/, "");
	if (samePage && url.hash) {
		window.history.pushState(null, "", `${url.pathname}${url.search}${url.hash}`);
		scrollToAnchor(url.hash);
		return;
	}
	navigate(link);
};
